<template>
  <div :id="drawerId" role="dialog" :aria-labelledby="drawerHeadingId">
    <transition name="fade">
      <div
        v-if="isDrawerOpen"
        class="confetti-drawer__overlay"
        data-testid="confetti-drawer-overlay"
        @click="closeDrawer"
        @keyup.enter="closeDrawer"
      ></div>
    </transition>

    <transition name="slide">
      <div v-if="isDrawerOpen" class="confetti-drawer__content">
        <header class="confetti-drawer__header">
          <h1
            :id="drawerHeadingId"
            data-testid="confetti-drawer-heading"
            class="confetti-drawer__header--main-heading"
            :title="headerText"
          >
            {{ headerText }}
          </h1>

          <button
            class="confetti-drawer__button--close"
            type="button"
            :aria-label="closeButtonAriaText"
            :aria-expanded="isDrawerOpen"
            :aria-controls="drawerId"
            @click="closeDrawer"
          >
            <img src="../assets/icons/close.svg" alt="" />
          </button>
        </header>

        <main class="confetti-drawer__main" data-testid="confetti-drawer-main">
          <slot />
        </main>
      </div>
    </transition>
  </div>
</template>

<script>
import { kebabCase } from "../utils";

export default {
  name: "ConfettiDrawer",
  props: {
    isDrawerOpen: Boolean,
    /**
     * Since we have multiple instances of the drawer component, and we want to
     * support aria-controls, we have to have a unique #id for each instance
     *
     * @example "user-login"
     */
    idSuffix: {
      type: String,
      default: "",
      required: true,
    },
    headerText: {
      type: String,
      default: "",
    },
    closeButtonAriaText: {
      type: String,
      default: "Close drawer",
    },
  },
  computed: {
    drawerId() {
      const id = `confetti-drawer-${this.idSuffix}`;
      const kebabCaseId = kebabCase(id);

      return kebabCaseId;
    },
    drawerHeadingId() {
      return `${this.drawerId}-heading`;
    },
  },
  watch: {
    isDrawerOpen(isOpen) {
      if (isOpen) {
        document.addEventListener("keyup", this.closeDrawerOnEscKeyup);
      } else {
        document.removeEventListener("keyup", this.closeDrawerOnEscKeyup);
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener("keyup", this.closeDrawerOnEscKeyup);
  },
  methods: {
    closeDrawer() {
      this.$emit("update:isDrawerOpen", false);
    },
    closeDrawerOnEscKeyup(keyboardEvent) {
      if (keyboardEvent.code === "Escape") {
        this.closeDrawer();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "confetti/scss/variables/layout";
@import "confetti/scss/variables/new-colors";
@import "confetti/scss/variables/shadows";
@import "confetti/scss/variables/z-index";

$animation-duration: 0.2s;

.confetti-drawer__overlay {
  background: rgba(25, 33, 52, 0.3);
  block-size: 100%;
  inline-size: 100%;
  inset-block-start: 0;
  inset-inline-start: 0;
  position: fixed;
  z-index: $modal-z-index;
}

.confetti-drawer__header {
  align-items: center;
  background: $color-red-900;
  block-size: $header-height;
  box-shadow: $drawer-box-shadow;
  color: $color-grey-100;
  display: flex;
  justify-content: space-between;
  padding: 1.6rem;
}

.confetti-drawer__header--main-heading {
  cursor: default;
  font-size: 1.8rem;
  font-weight: 600;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.confetti-drawer__button--close {
  all: unset;
  cursor: pointer;
}

.confetti-drawer__content {
  background: #fff;
  block-size: 100%;
  inline-size: 100%;
  inset-block-start: 0;
  inset-inline-end: 0;
  max-inline-size: 360px;
  position: fixed;
  z-index: $modal-z-index;
}

.confetti-drawer__main {
  padding: 1.6rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity $animation-duration;
}

.fade-leave-active {
  transition-delay: $animation-duration;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active {
  transition: transform $animation-duration ease-out;
  transition-delay: $animation-duration;
}

.slide-leave-active {
  transition: transform $animation-duration ease-in;
}

.slide-enter,
.slide-leave-to {
  transform: translateX(100%);
}
</style>
